import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Container, Grid, Typography, Paper, Button, 
  Divider, List, ListItem, ListItemIcon, ListItemText,
  IconButton, Snackbar, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, LinearProgress, Chip
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AlbumIcon from '@mui/icons-material/Album';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';

import Header from "../components/Header";
import FooterPlayer from "../components/FooterPlayer";

const KingOfGloryRelease = () => {
  const [activeTrack, setActiveTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [data, setData] = useState(null); 
  const audioRef = useRef(null);
  
  // Pre-order modal states
  const [openPreOrderModal, setOpenPreOrderModal] = useState(false);
  const [preOrderEmail, setPreOrderEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Inventory states
  const [vinylInventory, setVinylInventory] = useState(null);
  const [loadingInventory, setLoadingInventory] = useState(true);

  const vinylProductId = "69582275f72ab4313a68db70";

  const tracks = [
    { id: 1, title: "King of Glory (Vocal Mix)", version: "Vocal", src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/audio/releases/KingofGlory.mp3" },
    { id: 2, title: "King of Glory (King's Dub - coming soon)", version: "Dub", src: "#" }
  ];

  useEffect(() => {
    fetch("/api/landing")
      .then((res) => res.json())
      .then((json) => {
        setData(json);
      })
      .catch((err) => console.error("Error fetching header data:", err));
  }, []);

  // Fetch vinyl inventory
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const res = await fetch(`/albums/${vinylProductId}/inventory`);
        const result = await res.json();
        if (result.success) {
          setVinylInventory(result);
        }
      } catch (error) {
        console.error('Error fetching inventory:', error);
      } finally {
        setLoadingInventory(false);
      }
    };

    fetchInventory();
  }, [vinylProductId]);

  const handleTrackClick = (track) => {
    if (activeTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    } else {
      setActiveTrack(track);
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current.currentTime >= 30) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (activeTrack && audioRef.current) {
      audioRef.current.load();
      audioRef.current.play().catch(e => console.log("Playback interrupted"));
    }
  }, [activeTrack]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleBuyDownload = async () => {
    const productApi = "69582275f72ab4313a68db70"; 
    const payload = {
      product: "King of Glory Single",
      productType: "single",
      size: "Digital Download",
      designSpecs: "King of Glory (Vocal + Dub) Digital Mix",
      bodyColor: "N/A",
      productPrint: "N/A",
    };

    try {
      const res = await fetch(`/add/${productApi}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        if (result.cart) {
          localStorage.setItem("cart", JSON.stringify(result.cart));
        }
        showSnackbar('Digital download added to cart!');
        if (result.cartQty !== undefined) {
          setData(prev => ({ ...prev, cartQty: result.cartQty }));
        }
      } else {
        showSnackbar(result.message || "Failed to add to cart.", 'error');
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      showSnackbar('Error adding to cart. Please try again.', 'error');
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Open pre-order modal
  const handlePreOrderClick = () => {
    setOpenPreOrderModal(true);
    setPreOrderEmail('');
    setEmailError('');
  };

  // Handle pre-order submission
  const handlePreOrderSubmit = async () => {
    // Validate email
    if (!preOrderEmail) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(preOrderEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const payload = {
      product: "King of Glory 7\" Vinyl",
      productType: "single",
      size: "7\" Vinyl",
      format: "vinyl",
      designSpecs: "King of Glory Limited Edition 7\" Vinyl (Pre-Order)",
      bodyColor: "N/A",
      productPrint: "N/A",
      preOrderEmail: preOrderEmail,
      isPreOrder: true
    };

    try {
      const res = await fetch(`/add/${vinylProductId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      
      if (res.ok) {
        if (result.cart) {
          localStorage.setItem("cart", JSON.stringify(result.cart));
        }
        setOpenPreOrderModal(false);
        showSnackbar('Pre-order added! Check your email for updates.');
        
        // Update inventory count
        if (result.inventoryRemaining !== null && result.inventoryRemaining !== undefined) {
          setVinylInventory(prev => ({
            ...prev,
            inventory: result.inventoryRemaining,
            soldCount: prev.initialInventory - result.inventoryRemaining,
            percentSold: Math.round(((prev.initialInventory - result.inventoryRemaining) / prev.initialInventory) * 100)
          }));
        }
        
        if (result.cartQty !== undefined) {
          setData(prev => ({ ...prev, cartQty: result.cartQty }));
        }
      } else {
        showSnackbar(result.message || "Failed to add pre-order.", 'error');
      }
    } catch (err) {
      console.error("Error adding pre-order:", err);
      showSnackbar('Error processing pre-order. Please try again.', 'error');
    }
  };

  if (!data) return (
    <Box textAlign="center" py={8}><CircularProgress /><Typography mt={2}>Loading...</Typography></Box>
  );

  return (
    <Box sx={{ bgcolor: '#fdfdfd', minHeight: '100vh', pb: 10 }}>
      <Header
        menuItems={Array.isArray(data.menuItems) ? data.menuItems : []}
        logo="/images/mihu-logo-trans.png" 
        cartQty={typeof data.cartQty === "number" ? data.cartQty : 0}
      />

      <audio 
        ref={audioRef} 
        src={activeTrack?.src} 
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)} 
      />

      <Box sx={{ bgcolor: '#111', color: '#fff', pt: 12, pb: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ letterSpacing: 3, color: '#fb8c00' }}>
            New Release Out Now
          </Typography>

          <Box
            component="img"
            src="https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/releases/toots-akwerius-kingofglory.jpg"
            alt="King of Glory"
            sx={{
              width: "100%", maxWidth: 350, borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              display: "block", margin: "20px auto"
            }}
          />      
        </Container>

        <Container maxWidth="lg" sx={{ mt: 6 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>About the Single</Typography>
              <Divider sx={{ mb: 3, width: '60px', height: '4px', bgcolor: '#fb8c00' }} />
            
              <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 3, color: '#fff' }}>
                After their 2023 collaboration on <strong>
                  <a href="https://aquarianproject.bandcamp.com/track/his-name-feat-junior-toots" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>His Name</a>
                </strong> (co-produced by Circadian Riddims and mixed by Scientist), <strong>
                  <a href="https://juniortoots.com/" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>Junior Toots</a>
                </strong> and <strong>
                  <a href="https://akwerius.com" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>Akwerius</a>
                </strong> link up again for <strong>King of Glory</strong>.
                This song is a reminder that the Most High remains the sole source of balance and order in creation. It is a call for the world to return to a way of life governed by divine laws and principles: brother/sisterhood, compassion and justice. 
              </Typography> 

              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <video controls style={{ width: '100%', maxWidth: '500px', borderRadius: '8px' }}>
                  <source src="https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/newsletters/toots-vox.mp4" type="video/mp4" />
                </video>
              </Box>
            </Grid>

            <Grid item xs={12} md={7} lg={6}>
              <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayCircleOutlineIcon color="primary" /> Preview (30 Sec)
                </Typography>
                
                <List sx={{ mb: 4 }}>
                  {tracks.map((track) => (
                    <ListItem 
                      key={track.id} 
                      divider 
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleTrackClick(track)}>
                          {(activeTrack?.id === track.id && isPlaying) ? <PauseCircleOutlineIcon color="primary" /> : <PlayCircleOutlineIcon />}
                        </IconButton>
                      }
                    >
                      <ListItemIcon><AlbumIcon color={activeTrack?.id === track.id ? "primary" : "inherit"}/></ListItemIcon>
                      <ListItemText primary={track.title} secondary={track.version} />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingBagIcon color="primary" /> Store & Pre-Order
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large" 
                    startIcon={<DownloadIcon />} 
                    sx={{ bgcolor: '#111', py: 1.5 }}
                    onClick={handleBuyDownload}
                  >
                    Buy Digital Download ($1.29)
                  </Button>
                  
                  {/* Vinyl Pre-Order Button with Inventory Counter */}
                  <Box>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      size="large" 
                      startIcon={<AlbumIcon />} 
                      sx={{ py: 1.5, borderColor: '#fb8c00', color: '#fb8c00' }}
                      onClick={handlePreOrderClick}
                      disabled={vinylInventory && vinylInventory.inventory <= 0}
                    >
                      {vinylInventory && vinylInventory.inventory <= 0 
                        ? "Sold Out" 
                        : "Pre-Order Limited 7\" Vinyl ($14.99)"}
                    </Button>
                    
                    {/* Inventory Counter */}
                    {loadingInventory ? (
                      <Box sx={{ mt: 1, textAlign: 'center' }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : vinylInventory ? (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {vinylInventory.inventory} of {vinylInventory.initialInventory} available
                          </Typography>
                          <Chip 
                            label={`${vinylInventory.percentSold}% sold`}
                            size="small"
                            color={vinylInventory.percentSold > 75 ? "error" : vinylInventory.percentSold > 50 ? "warning" : "success"}
                          />
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={vinylInventory.percentSold} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 1,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: vinylInventory.percentSold > 75 ? '#d32f2f' : vinylInventory.percentSold > 50 ? '#ed6c02' : '#2e7d32'
                            }
                          }}
                        />
                        {vinylInventory.inventory <= 10 && vinylInventory.inventory > 0 && (
                          <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}
                          >
                            ⚠️ Only {vinylInventory.inventory} left!
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pre-Order Email Collection Modal */}
      <Dialog 
        open={openPreOrderModal} 
        onClose={() => setOpenPreOrderModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#111', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            <Typography variant="h6">Pre-Order: King of Glory 7" Vinyl</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We'll notify you when your vinyl ships! Enter your email to complete your pre-order.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Email Address"
            type="email"
            variant="outlined"
            value={preOrderEmail}
            onChange={(e) => {
              setPreOrderEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError || "We'll send you shipping updates and release notifications"}
            sx={{ mb: 2 }}
          />
          <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #fb8c00' }}>
            <Typography variant="body2">
              <strong>Pre-Order Details:</strong><br />
              • Limited edition 7" vinyl<br />
              • Estimated ship date: 7/23/2026<br />
              • Price: $14.99 + shipping<br />
              • You'll be charged when item ships
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenPreOrderModal(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handlePreOrderSubmit} 
            variant="contained" 
            sx={{ bgcolor: '#fb8c00' }}
            startIcon={<AlbumIcon />}
          >
            Complete Pre-Order
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setOpenSnackbar(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <FooterPlayer />
    </Box>
  );
};

export default KingOfGloryRelease;
// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box, Container, Grid, Typography, Paper, Button, 
//   Divider, List, ListItem, ListItemIcon, ListItemText,
//   IconButton
// } from '@mui/material';
// import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
// import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
// import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
// import AlbumIcon from '@mui/icons-material/Album';
// import DownloadIcon from '@mui/icons-material/Download';
// import ShareIcon from '@mui/icons-material/Share';

// const KingOfGloryRelease = () => {
//   const [activeTrack, setActiveTrack] = useState(null);
//   const [isPlaying, setIsPlaying] = useState(false);
//   const audioRef = useRef(null);

//   const tracks = [
//     { id: 1, title: "King of Glory (Vocal Mix)", version: "Vocal", src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/audio/releases/KingofGlory.mp3" },
//     { id: 2, title: "King of Glory (King's Dub)", version: "Dub", src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/audio/releases/KingofGloryDub.mp3" }
//   ];

//   // Logic to handle playing the audio
//   const handleTrackClick = (track) => {
//     if (activeTrack?.id === track.id) {
//       if (isPlaying) {
//         audioRef.current.pause();
//         setIsPlaying(false);
//       } else {
//         audioRef.current.play();
//         setIsPlaying(true);
//       }
//     } else {
//       setActiveTrack(track);
//       setIsPlaying(true);
//     }
//   };

//   useEffect(() => {
//     if (activeTrack && audioRef.current) {
//       audioRef.current.load();
//       audioRef.current.play().catch(e => console.log("Playback interrupted"));
//     }
//   }, [activeTrack]);

//   return (
//     <Box sx={{ bgcolor: '#111',  color: '#fff', minHeight: '100vh', pb: 10 }}>
//       {/* Audio Element */}
//       <audio ref={audioRef} src={activeTrack?.src} onEnded={() => setIsPlaying(false)} />

//       {/* 1. HEADER SECTION */}
//       <Box sx={{ bgcolor: '#111', color: '#fff', py: 6, textAlign: 'center' }}>
//         <Container maxWidth="md">
//           <Typography variant="overline" sx={{ letterSpacing: 3, color: '#fb8c00' }}>
//             New Release Out Now
//           </Typography>
          
//           <Box
//             component="img"
//             src="/images/releases/toots-akwerius-kingofglory.jpg"
//             alt="King of Glory"
//             sx={{
//               width: "100%",
//               maxWidth: 450,
//               borderRadius: 2,
//               boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
//               display: "block",
//               margin: "20px auto", // Centered using margin auto
//             }}
//           />      
            
//         </Container>
//       </Box>

//       {/* Spacing adjusted (mt: 6) so title isn't hidden by header */}
//       <Container maxWidth="lg" sx={{ mt: 6 }}>
//         <Grid container spacing={4} justifyContent="center">
          
//           {/* 3. PRESS RELEASE & ARTWORK */}
//           <Grid item xs={12} md={8}>
//             <Typography variant="h4" fontWeight="bold" gutterBottom>
//               About the Single
//             </Typography>
//             <Divider sx={{ mb: 3, width: '60px', height: '4px', bgcolor: '#fb8c00' }} />
            
//             <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 3, color: '#fff' }}>
//               After their 2023 collaboration on <strong>
//                 <a href="https://aquarianproject.bandcamp.com/track/his-name-feat-junior-toots" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>His Name</a>
//               </strong> (co-produced by Circadian Riddims and mixed by Scientist), <strong>
//                 <a href="https://juniortoots.com/" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>Junior Toots</a>
//               </strong> and <strong>
//                 <a href="https://akwerius.com" target="_blank" rel="noreferrer" style={{ color: 'dodgerblue' }}>Akwerius</a>
//               </strong> link up again for <strong>King of Glory</strong>.
//               This song is a reminder that the Most High remains the sole source of balance and order in creation. It is a call for the world to return to a way of life governed by divine laws and principles: brother/sisterhood, compassion and justice. 
//               {/* As Toots establishes in his intro verse quoting from Psalm 18: <br />
//               <i>
//                 Jah is my rock and my fortress<br />
//                 He is my shield and my buckler<br />
//                 when there is no light<br />
//                 He brings me out of the darkness<br />
//                 His Name is Holy<br />
//                 He is the King of Glory
//               </i>*/}
//             </Typography> 
            
//             <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
//               <video 
//                 controls 
//                 style={{
//                   width: '100%',
//                   maxWidth: '500px',
//                   borderRadius: '8px'
//                 }}
//               >
//                 <source src="https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/newsletters/toots-vox.mp4" type="video/mp4" />
//               </video>
//             </Box>
//           </Grid>

//           {/* 4. AUDIO PLAYER & SHOPPING (Centered via Grid props) */}
//           <Grid item xs={12} md={7} lg={6}>
//             <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 4 }}>
//               <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <PlayCircleOutlineIcon color="primary" /> Listen & Preview
//               </Typography>
              
//               <List sx={{ mb: 4 }}>
//                 {tracks.map((track) => (
//                   <ListItem 
//                     key={track.id} 
//                     divider 
//                     secondaryAction={
//                       <IconButton edge="end" onClick={() => handleTrackClick(track)}>
//                         {(activeTrack?.id === track.id && isPlaying) ? <PauseCircleOutlineIcon color="primary" /> : <PlayCircleOutlineIcon />}
//                       </IconButton>
//                     }
//                   >
//                     <ListItemIcon><AlbumIcon color={activeTrack?.id === track.id ? "primary" : "inherit"}/></ListItemIcon>
//                     <ListItemText primary={track.title} secondary={track.version} />
//                   </ListItem>
//                 ))}
//               </List>

//               <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
//                 <ShoppingBagIcon color="primary" /> Store & Pre-Order
//               </Typography>
              
//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                 <Button variant="contained" fullWidth size="large" startIcon={<DownloadIcon />} sx={{ bgcolor: '#111', py: 1.5 }}>
//                   Buy Digital Download ($1.29)
//                 </Button>
                
//                 <Button variant="outlined" fullWidth size="large" startIcon={<AlbumIcon />} sx={{ py: 1.5, borderColor: '#fb8c00', color: '#fb8c00' }}>
//                   Pre-Order Limited 7" Vinyl
//                 </Button>

//                 <Button variant="text" startIcon={<ShareIcon />}>
//                   Share Release
//                 </Button>
//               </Box>

//               <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center', color: '#888' }}>
//                 Release Date: {new Date().toLocaleDateString()} | Label: MIHU Records
//               </Typography>
//             </Paper>
//           </Grid>

//         </Grid>
//       </Container>
//     </Box>
//   );
// };

// export default KingOfGloryRelease;